import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useBoard } from "./BoardContext";
import { useOthers } from "./hooks";

interface Notification {
  id: string;
  type: string;
  boardId: string | null;
  cardId: string | null;
  actorName: string | null;
  boardTitle: string | null;
  excerpt: string | null;
  readAt: string | null;
  createdAt: string;
}
interface Activity {
  id: string;
  type: string;
  actorName: string | null;
  added: number;
  removed: number;
  detail: string | null;
  updatedAt: string;
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(document.documentElement.lang || "es", {
    dateStyle: "short",
    timeStyle: "short",
  });

/** Bell drawer: my notifications, this board's activity, and who is online now. */
export function ActivityPanel({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<"notifications" | "activity" | "online">("notifications");
  const [items, setItems] = useState<Notification[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const load = useCallback(async () => {
    const [n, a] = await Promise.all([
      api<{ items: Notification[] }>("/api/notifications"),
      api<Activity[]>(`/api/boards/${boardId}/activity`),
    ]);
    setItems(n.items);
    setActivity(a);
  }, [boardId]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (tab === "notifications" && items.some((i) => !i.readAt)) {
      const timer = setTimeout(
        () =>
          api("/api/notifications/read", { method: "POST", json: {} }).then(() =>
            window.dispatchEvent(new CustomEvent("mn:notifications-read")),
          ),
        1500,
      );
      return () => clearTimeout(timer);
    }
  }, [tab, items]);
  const describe = (n: Notification) =>
    n.type === "mention"
      ? t.notif.mention(n.actorName ?? "", n.boardTitle ?? "")
      : n.type === "comment"
        ? t.notif.comment(n.actorName ?? "", n.boardTitle ?? "")
        : t.notif.shared(n.actorName ?? "", n.boardTitle ?? "");
  return (
    <aside className="absolute bottom-0 right-0 top-0 z-30 flex max-md:w-full md:w-96 flex-col border-l border-border bg-panel shadow-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="display text-xl">.{t.activity}</span>
        <button type="button" onClick={onClose} className="text-muted hover:text-text">
          ✕
        </button>
      </div>
      <div className="grid grid-cols-3 border-b border-border text-sm">
        {(["notifications", "activity", "online"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`py-2 ${tab === k ? "bg-accent text-bg" : "text-muted"}`}
          >
            {t.tabs[k]}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-auto">
        {tab === "notifications" ? (
          <ul>
            {items.length === 0 ? <li className="p-4 text-sm text-dim">—</li> : null}
            {items.map((n) => (
              <li
                key={n.id}
                className={`border-b border-border px-4 py-3 text-sm ${n.readAt ? "" : "bg-panel-2"}`}
              >
                {n.boardId ? (
                  <Link
                    to={`/b/${n.boardId}${n.cardId ? `?card=${n.cardId}` : ""}`}
                    className="block hover:underline"
                  >
                    {describe(n)}
                  </Link>
                ) : (
                  describe(n)
                )}
                {n.excerpt ? (
                  <div className="mt-1 line-clamp-2 text-[12px] text-muted">{n.excerpt}</div>
                ) : null}
                <div className="mt-1 text-[11px] text-dim">{when(n.createdAt)}</div>
              </li>
            ))}
          </ul>
        ) : null}
        {tab === "activity" ? (
          <ul>
            {activity.length === 0 ? <li className="p-4 text-sm text-dim">—</li> : null}
            {activity.map((a) => (
              <li key={a.id} className="border-b border-border px-4 py-3 text-sm">
                <span className="font-semibold">{a.actorName ?? "—"}</span>{" "}
                {a.type === "edits"
                  ? t.act.edits(a.added, a.removed)
                  : a.type === "comment"
                    ? t.act.comment
                    : a.type === "joined"
                      ? t.act.joined
                      : a.type}
                {a.detail ? <div className="mt-1 line-clamp-2 text-[12px] text-muted">{a.detail}</div> : null}
                <div className="mt-1 text-[11px] text-dim">{when(a.updatedAt)}</div>
              </li>
            ))}
          </ul>
        ) : null}
        {tab === "online" ? <Online /> : null}
      </div>
    </aside>
  );
}

function Online() {
  const { handle, user } = useBoard();
  const t = useT();
  const others = useOthers(handle.provider.awareness!);
  const people = new Map<string, { name: string; color: string }>();
  people.set(user.id, { name: `${user.name} (${t.you})`, color: user.color });
  for (const st of others.values()) if (st.user) people.set(st.user.id, st.user);
  return (
    <ul>
      {[...people.values()].map((p) => (
        <li key={p.name} className="flex items-center gap-2 border-b border-border px-4 py-2 text-sm">
          <span className="size-2.5 rounded-full" style={{ background: p.color }} /> {p.name}
        </li>
      ))}
    </ul>
  );
}

/** Header bell with unread count; polls every 30 s and on focus. */
export function Bell({ onClick }: { onClick: () => void }) {
  const t = useT();
  const [unread, setUnread] = useState(0);
  const refresh = useCallback(
    () =>
      api<{ unread: number }>("/api/notifications")
        .then((r) => setUnread(r.unread))
        .catch(() => {}),
    [],
  );
  useEffect(() => {
    void refresh();
    const id = setInterval(refresh, 30_000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("mn:notifications-read", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mn:notifications-read", onFocus);
    };
  }, [refresh]);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative text-muted hover:text-text"
      title={t.activity}
    >
      🔔
      {unread > 0 ? (
        <span className="absolute -right-2 -top-2 rounded-full bg-[#f4511c] px-1.5 text-[10px] font-bold text-white">
          {unread}
        </span>
      ) : null}
    </button>
  );
}
